// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { IButtonStyles } from "@fluentui/react";
import type { IRawStyle, ITheme } from "@fluentui/react/lib/Styling";
import {
  concatStyleSets,
  HighContrastSelector,
} from "@fluentui/react/lib/Styling";
import { memoizeFunction } from "@fluentui/react/lib/Utilities";

import type { StandardControlSemanticColors } from "@1js/acui-common";
import {
  commonHighContrastButtonRestStyles,
  commonHighContrastDisabledStyles,
  commonHighContrastHoverFocusStyles,
  commonHighContrastOutlineFocusedDisabled,
  commonHighContrastOutlineHoverDisabled,
  commonHighContrastOutlineHoverFocus,
  commonHighContrastOutlineToggledOn,
  getButtonStateStyles,
  getStandardControlSemanticColorsWithTheme,
  hiddenContentStyle,
  textContainerClassName,
} from "@1js/acui-common";
import { buttonIconWidth } from "./AppBaseButtonConstants";

export function getDisabledAppToggleButtonHoverActiveStyles(
  theme: ITheme
): IRawStyle {
  const standardControlSemanticColors: StandardControlSemanticColors =
    getStandardControlSemanticColorsWithTheme(theme);
  return {
    backgroundColor: standardControlSemanticColors.toggledBackgroundColor,
    color: standardControlSemanticColors.textColor,
    border: `1px solid transparent`,
  };
}

export const getAppBaseButtonStyles = memoizeFunction(
  (
    theme: ITheme,
    customStyles?: IButtonStyles,
    hasIcon: boolean = false
  ): IButtonStyles => {
    const standardControlSemanticColors: StandardControlSemanticColors =
      getStandardControlSemanticColorsWithTheme(theme);
    const buttonStyles: IButtonStyles = {
      root: [
        {
          height: 32,
          backgroundColor: standardControlSemanticColors.defaultBackgroundColor,
          color: standardControlSemanticColors.textColor,
          padding: "0 11px",
          boxSizing: "border-box",
          border: `1px solid ${standardControlSemanticColors.borderColor}`,
          userSelect: "none",
          display: "inline-block",
          textDecoration: "none",
          textAlign: "center",
          cursor: "default",
          verticalAlign: "top",
          borderRadius: 2,
          fontSize: 14,
          fontWeight: 600,
          position: "relative",
        },
        commonHighContrastButtonRestStyles,
        getButtonStateStyles(
          theme,
          undefined /* expanded */,
          false /* disabled */
        ),
      ],

      flexContainer: {
        display: "flex",
        height: "100%",
        flexWrap: "nowrap",
        justifyContent: "center",
        alignItems: "center",
      },

      rootDisabled: [
        {
          color: standardControlSemanticColors.textDisabledColor,
          backgroundColor:
            standardControlSemanticColors.disabledBackgroundColor,
          border: `1px solid ${standardControlSemanticColors.disabledBorderColor}`,
          pointerEvents: "none",
          cursor: "default",
          selectors: {
            ":hover": [
              {
                outline: 0,
                backgroundColor:
                  standardControlSemanticColors.disabledBackgroundColor,
              },
              commonHighContrastOutlineHoverDisabled,
            ],
            ":focus": commonHighContrastOutlineFocusedDisabled,
            ":active": {
              outline: 0,
              backgroundColor:
                standardControlSemanticColors.disabledBackgroundColor,
            },
          },
        },
        commonHighContrastDisabledStyles,
        getButtonStateStyles(
          theme,
          undefined /* expanded */,
          true /* disabled */
        ),
      ],
      rootHovered: [
        commonHighContrastHoverFocusStyles,
        commonHighContrastOutlineHoverFocus,
      ],
      rootFocused: [
        commonHighContrastHoverFocusStyles,
        commonHighContrastOutlineHoverFocus,
      ],
      rootChecked: [
        {
          backgroundColor: standardControlSemanticColors.toggledBackgroundColor,
        },
        commonHighContrastHoverFocusStyles,
        commonHighContrastOutlineToggledOn,
      ],
      rootCheckedHovered: [
        {
          backgroundColor: standardControlSemanticColors.hoverBackgroundColor,
          color: standardControlSemanticColors.textColor,
        },
        commonHighContrastHoverFocusStyles,
        commonHighContrastOutlineHoverFocus,
      ],
      rootCheckedDisabled: {
        backgroundColor: standardControlSemanticColors.toggledBackgroundColor,
        selectors: {
          ":hover": [
            getDisabledAppToggleButtonHoverActiveStyles(theme),
            commonHighContrastOutlineHoverDisabled,
          ],
          ":active": [
            getDisabledAppToggleButtonHoverActiveStyles(theme),
            commonHighContrastHoverFocusStyles,
          ],
          ":focus": [commonHighContrastOutlineFocusedDisabled],
        },
      },
      rootExpanded: [
        {
          backgroundColor:
            standardControlSemanticColors.expandedBackgroundColor,
          selectors: {
            ":hover": [
              commonHighContrastOutlineHoverFocus,
              commonHighContrastHoverFocusStyles,
            ],
          },
        },
        commonHighContrastHoverFocusStyles,
      ],
      label: [
        textContainerClassName,
        {
          lineHeight: "100%",
          textAlign: "left",
          marginLeft: hasIcon ? 9 : 8,
          marginRight: 8,
          display: "block",
        },
      ],
      labelDisabled: [
        {
          color: standardControlSemanticColors.textDisabledColor,
        },
        commonHighContrastDisabledStyles,
      ],
      icon: {
        width: buttonIconWidth,
        height: 20,
        lineHeight: 20,
        margin: 0,
        fill: standardControlSemanticColors.textColor,
      },

      iconDisabled: {
        opacity: 0.4,
        selectors: {
          [HighContrastSelector]: {
            opacity: 1,
          },
        },
      },

      textContainer: {
        flexGrow: 1,
      },

      screenReaderText: hiddenContentStyle,
    };
    return concatStyleSets(buttonStyles, customStyles);
  }
);
